import {Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse} from '@angular/common/http';
import {from, Observable, throwError, BehaviorSubject} from 'rxjs';
import {AuthenticationService} from '../authentication.service';
import {PrismHeaders} from '../../enums';
import {catchError, switchMap, filter, take} from 'rxjs/operators';
import {environment} from '../../../environments';
import {getAuth} from 'firebase/auth';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    constructor(public authService: AuthenticationService) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let headers;
        const serviceToken = this.authService.sessionTokenRaw;

        if (req.method === 'JSONP') {
            return next.handle(req);
        }

        if (this.isPublicAlbumRoute()) {
            return next.handle(req);
        }

        // Skip authentication for static assets
        if (this.isStaticAsset(req.url)) {
            return next.handle(req);
        }

        if (!req.headers.has(PrismHeaders.SkipHeaders)) {
            if (!serviceToken) {
                return from(this.authService.sessionToken$)
                    .pipe(
                        switchMap(token => {
                            if (!!token) {
                                headers = req.headers
                                    .set('Content-Type', 'application/json')
                                    .set('Access-Control-Allow-Origin', `${environment.baseUrl}`)
                                    .set(PrismHeaders.Authorization, `Bearer ${token}`);

                                const reqClone = req.clone({
                                    headers
                                });

                                return next.handle(reqClone).pipe(
                                    catchError((error: HttpErrorResponse) => this.handleAuthError(error, reqClone, next))
                                );
                            } else {
                                return throwError(() => new Error('No session token available'));
                            }
                        })
                    );
            } else {
                headers = req.headers
                    .set('Content-Type', 'application/json')
                    .set('Access-Control-Allow-Origin', `${environment.baseUrl}`)
                    .set(PrismHeaders.Authorization, `Bearer ${serviceToken}`);

                let reqClone = req.clone({
                    headers
                });

                if (this.isStaticAsset(req.url)) {
                    console.log('intercept svg assets---- ')
                    console.log(req.url)
                    console.log(req.method)

                    reqClone = req.clone({});
                }

                return next.handle(reqClone).pipe(
                    catchError((error: HttpErrorResponse) => this.handleAuthError(error, reqClone, next))
                );
            }

        } else {
            headers = req.headers
                .delete(PrismHeaders.SkipHeaders);

            const reqClone = req.clone({
                headers
            });
            return next.handle(reqClone);
        }
    }

    private handleAuthError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (error.status === 401) {
            if (!this.isRefreshing) {
                this.isRefreshing = true;
                this.refreshTokenSubject.next(null);

                const auth = getAuth();
                const currentUser = auth.currentUser;

                if (currentUser && !currentUser.isAnonymous) {
                    console.log("Attempting to refresh token for user:", currentUser.uid);
                    return from(currentUser.getIdToken(true)).pipe(
                        switchMap(newToken => {
                            if (!newToken) {
                                console.error('Token refresh returned null/empty token');
                                this.isRefreshing = false;
                                // Don't logout - just fail the request
                                return throwError(() => error);
                            }
                            
                            console.log('Token successfully refreshed');
                            this.isRefreshing = false;
                            this.authService['sessionTokenSource'].next(newToken);
                            this.refreshTokenSubject.next(newToken);
                            
                            const newHeaders = req.headers.set(PrismHeaders.Authorization, `Bearer ${newToken}`);
                            const newReq = req.clone({headers: newHeaders});
                            return next.handle(newReq);
                        }),
                        catchError(refreshError => {
                            this.isRefreshing = false;
                            console.error('Token refresh failed with error:', refreshError);
                            console.error('Error code:', refreshError?.code);
                            console.error('Error message:', refreshError?.message);
                            
                            // Only logout for specific auth errors, not network or temporary issues
                            if (refreshError?.code === 'auth/user-token-expired' || 
                                refreshError?.code === 'auth/user-disabled' ||
                                refreshError?.code === 'auth/user-not-found') {
                                console.error('Critical auth error - logging out user');
                                this.authService.logout();
                            } else {
                                console.warn('Temporary auth error - not logging out');
                            }
                            
                            return throwError(() => error);
                        })
                    );
                } else {
                    console.warn('No authenticated user found yet - auth may not be initialized');
                    this.isRefreshing = false;
                    // Don't logout if currentUser is null - auth might just not be initialized yet
                    // Let the retry logic in services handle this
                    return throwError(() => error);
                }
            } else {
                // Queue requests while token is being refreshed
                return this.refreshTokenSubject.pipe(
                    filter(token => token != null),
                    take(1),
                    switchMap(token => {
                        const newHeaders = req.headers.set(PrismHeaders.Authorization, `Bearer ${token}`);
                        const newReq = req.clone({headers: newHeaders});
                        return next.handle(newReq);
                    })
                );
            }
        }
        
        return throwError(() => error);
    }

    private isPublicAlbumRoute(): boolean {
        return window.location.pathname.startsWith('/agent/');
    }

    private isStaticAsset(url: string): boolean {
        // Check if the URL is for a static asset
        const staticAssetPatterns = [
            '/assets/',
            '.svg',
            '.png',
            '.jpg',
            '.jpeg',
            '.gif',
            '.webp',
            '.ico',
            '.css',
            '.js',
            '.woff',
            '.woff2',
            '.ttf',
            '.eot'
        ];

        return staticAssetPatterns.some(pattern => url.includes(pattern));
    }

}
